/**
 * 安全的存储适配器
 * @description 提供对 localStorage 和 sessionStorage 的安全访问，避免 SSR/测试环境下的错误
 * @module src/utils/v2/storage
 * @since 2026-01-14
 */

import type { StorageLike } from 'pinia-plugin-persistedstate'

/**
 * 检查 localStorage 是否可用
 */
function isLocalStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false
    }
    // 尝试写入和读取测试值，确保 localStorage 真正可用
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  }
  catch {
    return false
  }
}

/**
 * 检查 sessionStorage 是否可用
 */
function isSessionStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return false
    }
    const testKey = '__storage_test__'
    sessionStorage.setItem(testKey, testKey)
    sessionStorage.removeItem(testKey)
    return true
  }
  catch {
    return false
  }
}

/**
 * 内存存储回退方案
 * @description 当 localStorage 不可用时使用的内存存储
 */
class MemoryStorage implements StorageLike {
  private store = new Map<string, string>()

  getItem(key: string): string | null {
    return this.store.get(key) ?? null
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value)
  }

  clear(): void {
    this.store.clear()
  }

  get length(): number {
    return this.store.size
  }

  key(index: number): string | null {
    const keys = Array.from(this.store.keys())
    return keys[index] ?? null
  }
}

// 内存存储单例
const memoryStorage = new MemoryStorage()

/**
 * 安全的 localStorage 适配器
 * @description 用于 Pinia 持久化插件，自动回退到内存存储
 */
export const safeLocalStorage: StorageLike = {
  getItem(key: string): string | null {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key)
    }
    return memoryStorage.getItem(key)
  },
  setItem(key: string, value: string): void {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value)
    }
    else {
      memoryStorage.setItem(key, value)
    }
  },
}

/**
 * 安全的 sessionStorage 适配器
 * @description 用于需要会话级别存储的场景
 */
export const safeSessionStorage: StorageLike = {
  getItem(key: string): string | null {
    if (isSessionStorageAvailable()) {
      return sessionStorage.getItem(key)
    }
    return memoryStorage.getItem(key)
  },
  setItem(key: string, value: string): void {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem(key, value)
    }
    else {
      memoryStorage.setItem(key, value)
    }
  },
}

/**
 * 导出内存存储实例（用于测试）
 */
export { isLocalStorageAvailable, isSessionStorageAvailable, memoryStorage }
